import React, { useState, useEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  FaCalendarAlt,
  FaUser,
  FaBuilding,
  FaPercent,
  FaAngleDown,
  FaAngleUp,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
} from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { MdBedroomParent } from 'react-icons/md';
import {
  setModifyDates,
  setAvailableFactors,
  setSelectedBedType,
  setSelectedAddons,
  setSelectedFactorIds,
} from '../Walk-In/redux/action';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL || 'http://localhost:9000';
const BQ_PRICING_URL = process.env.REACT_APP_BQ_PRICING_URL || 'http://127.0.0.1:8001';

const HIDDEN_ADDON_FACTOR_NAMES = ['number of guest', 'no of people', 'number of people'];

const isHiddenAddon = (factor) => {
  const name = (factor?.factorname || '').toLowerCase().trim();
  return HIDDEN_ADDON_FACTOR_NAMES.some((hidden) => name.includes(hidden));
};

const formatDateParam = (d) => {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatCurrency = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const RoomDates = () => {
  const dispatch = useDispatch();

  const { checkInDate, checkOutDate, daysCount } = useSelector(
    (state) => state.selectedModifyDates
  );

  const { adults, children, rooms, childrenAges } = useSelector((state) => state.formDetails);

  const bookingData = useSelector((state) => state.booking.selectedBooking);

  const bookingFactors = useSelector((state) => state.factors?.bookingFactors || []);
  const availableFactors = useSelector((state) => state.factors?.availableFactors || []);
  const selectedBedType = useSelector((state) => state.factors?.selectedBedType || null);
  const selectedAddons = useSelector((state) => state.factors?.selectedAddons || []);

  const { corporateCode, seniorCitizenDiscount, promoCode, isSpecialRateApplied } = useSelector(
    (state) => state.specialRates || {}
  );

  const [showCalendar, setShowCalendar] = useState(false);
  const [showTravellersDropdown, setShowTravellersDropdown] = useState(false);
  const [showRoomsDropdown, setShowRoomsDropdown] = useState(false);
  const [showSpecialRateDropdown, setShowSpecialRateDropdown] = useState(false);

  const [localCorporateCode, setLocalCorporateCode] = useState(corporateCode || '');
  const [localPromoCode, setLocalPromoCode] = useState(promoCode || '');
  const [localSeniorDiscount, setLocalSeniorDiscount] = useState(seniorCitizenDiscount || false);
  const [showDetails, setShowDetails] = useState(false);

  const [isValidatingContract, setIsValidatingContract] = useState(false);
  const [contractValidation, setContractValidation] = useState({
    isValid: null,
    message: '',
    data: null,
  });

  const [availabilityPricing, setAvailabilityPricing] = useState(null);
  const [isLoadingPricing, setIsLoadingPricing] = useState(false);

  const calendarRef = useRef(null);
  const travellersRef = useRef(null);
  const roomsRef = useRef(null);
  const specialRateRef = useRef(null);

  // ── 1. Initialise dates from booking ────────────────────────────────
  useEffect(() => {
    if (bookingData?.checkindate && bookingData?.checkoutdate) {
      const checkinDate = new Date(bookingData.checkindate);
      const checkoutDate = new Date(bookingData.checkoutdate);

      dispatch(setModifyDates([checkinDate, checkoutDate]));

      dispatch({
        type: 'SET_ADULTS',
        payload: bookingData.number_of_guests || 1,
      });
      dispatch({ type: 'SET_CHILDREN', payload: 0 });
      dispatch({ type: 'SET_CHILDREN_AGES', payload: [] });
      dispatch({ type: 'SET_ROOMS', payload: bookingData.rooms || 1 });
    } else {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      dispatch(setModifyDates([today, tomorrow]));
    }
  }, [bookingData, dispatch]);

  // ── 2. Fetch available factors ──────────────────────────────────────
  useEffect(() => {
    const fetchFactors = async () => {
      try {
        const res = await fetch(`${BQ_PRICING_URL}/pricing/api/get-factors`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();
        dispatch(setAvailableFactors(Array.isArray(data) ? data : []));
      } catch (err) {
        console.error('Error fetching factors:', err);
      }
    };
    if (availableFactors.length === 0) fetchFactors();
  }, [dispatch, availableFactors.length]);

  // ── 3. Pre-select factors from booking ──────────────────────────────
  useEffect(() => {
    if (!bookingFactors || bookingFactors.length === 0) return;

    const bedTypeFromBooking = bookingFactors.find((f) => f.factor_type === 'bedtype');
    // Include discount factors here too — they must survive the modify flow so
    // they keep getting passed to the backend. They are hidden only in the UI
    // (see `addonFactors` below).
    const addonsFromBooking = bookingFactors.filter(
      (f) => f.factor_type !== 'bedtype' && !isHiddenAddon({ factorname: f.factor_name })
    );

    if (bedTypeFromBooking) {
      dispatch(
        setSelectedBedType({
          factorid: bedTypeFromBooking.factor_id,
          factorname: bedTypeFromBooking.factor_name,
          value: bedTypeFromBooking.value_applied,
          value_type: bedTypeFromBooking.value_type,
          factor_type: bedTypeFromBooking.factor_type,
        })
      );
    }

    if (addonsFromBooking.length > 0) {
      dispatch(
        setSelectedAddons(
          addonsFromBooking.map((f) => ({
            factorid: f.factor_id,
            factorname: f.factor_name,
            value: f.value_applied,
            value_type: f.value_type,
            factor_type: f.factor_type,
          }))
        )
      );
    }
  }, [bookingFactors, dispatch]);

  // ── 4. Sync special-rate local state ────────────────────────────────
  useEffect(() => {
    setLocalCorporateCode(corporateCode || '');
    setLocalPromoCode(promoCode || '');
    setLocalSeniorDiscount(seniorCitizenDiscount || false);
  }, [corporateCode, promoCode, seniorCitizenDiscount]);

  useEffect(() => {
    if (contractValidation.isValid === true && !corporateCode) {
      setContractValidation({ isValid: null, message: '', data: null });
    }
  }, [corporateCode]);

  // ── 5. Derived factor lists ─────────────────────────────────────────
  const bedTypeFactors = availableFactors.filter(
    (f) => f.factor_type === 'bedtype' && f.isactive !== false
  );

  // ⭐ UI-only filter — discount factors are hidden from the Add-ons & Features row
  const addonFactors = availableFactors.filter(
    (f) =>
      f.show_in_bq === true &&
      f.factor_type !== 'bedtype' &&
      f.factor_type !== 'discount' &&
      f.isactive !== false &&
      !isHiddenAddon(f)
  );

  // Set of all bed-type factor ids (for de-duping)
  const bedTypeFactorIdSet = new Set(bedTypeFactors.map((f) => Number(f.factorid)));

  // Effective factor ids sent to availability API
  const effectiveFactorIds = (() => {
    const ids = new Set();

    const currentBedTypeId = selectedBedType?.factorid ? Number(selectedBedType.factorid) : null;
    if (currentBedTypeId) ids.add(currentBedTypeId);

    for (const a of selectedAddons) {
      const aid = Number(a.factorid);
      if (bedTypeFactorIdSet.has(aid) && aid !== currentBedTypeId) continue;
      ids.add(aid);
    }

    if (ids.size === 0) {
      for (const f of bookingFactors) {
        // Include discount factor ids too — the availability API needs the
        // full picture. UI still hides them via `addonFactors`.
        const fid = Number(f.factor_id);
        if (Number.isFinite(fid)) ids.add(fid);
      }
    }

    return Array.from(ids).filter((n) => Number.isFinite(n));
  })();

  // ── 6. Fetch availability pricing on date/factor change ────────────
  useEffect(() => {
    let isMounted = true;

    const fetchAvailability = async () => {
      try {
        if (!checkInDate || !checkOutDate) return;

        setIsLoadingPricing(true);
        const params = new URLSearchParams();
        params.set('checkin', formatDateParam(checkInDate));
        params.set('checkout', formatDateParam(checkOutDate));
        if (effectiveFactorIds.length > 0) {
          params.set('factor_ids', effectiveFactorIds.join(','));
        }

        const res = await fetch(
          `${BQ_BASE_URL}/bq/api/availability/check-dates/all?${params.toString()}`,
          { headers: { Accept: 'application/json' } }
        );
        if (!res.ok) throw new Error('Availability request failed');

        const data = await res.json();
        if (!isMounted) return;
        setAvailabilityPricing(data);
        dispatch({ type: 'SET_AVAILABILITY_PRICING', payload: data });
      } catch (err) {
        console.error('Error fetching availability pricing:', err);
        if (isMounted) setAvailabilityPricing(null);
      } finally {
        if (isMounted) setIsLoadingPricing(false);
      }
    };

    fetchAvailability();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInDate, checkOutDate, effectiveFactorIds.join(',')]);

  // Current-room availability + pricing
  const currentRoomTypeName = bookingData?.room_type || bookingData?.roomtypename || 'Comfort';

  const currentRoomPricing = (() => {
    if (!availabilityPricing?.room_types) return null;
    return (
      availabilityPricing.room_types.find(
        (rt) => rt.room_type === currentRoomTypeName || rt.roomtypeid === bookingData?.roomtypeid
      ) || null
    );
  })();

  const getFactorContributionForCurrentRoom = (factorId) => {
    if (!currentRoomPricing?.factor_breakdown) return 0;
    const hit = currentRoomPricing.factor_breakdown.find(
      (fb) => Number(fb.factorid) === Number(factorId)
    );
    return hit ? Number(hit.contribution || 0) : 0;
  };

  const currentRoomAvailableCount = currentRoomPricing?.available_rooms ?? null;
  const currentRoomTotalCount = currentRoomPricing?.total_rooms ?? null;

  // ── 7. Factor handlers ──────────────────────────────────────────────
  const handleBedTypeSelect = (factor) => {
    const newBedTypeId = Number(factor.factorid);

    // Strip any other bed-type factor that may be in addons
    const cleanedAddons = selectedAddons.filter((a) => !bedTypeFactorIdSet.has(Number(a.factorid)));

    const payload = {
      factorid: factor.factorid,
      factorname: factor.factorname,
      value: factor.default_value || 0,
      value_type: factor.value_type,
      factor_type: factor.factor_type,
    };
    dispatch(setSelectedBedType(payload));
    dispatch(setSelectedAddons(cleanedAddons));

    const ids = [newBedTypeId, ...cleanedAddons.map((a) => Number(a.factorid))];
    dispatch(setSelectedFactorIds(ids));
  };

  const handleAddonToggle = (factor) => {
    const exists = selectedAddons.some((a) => a.factorid === factor.factorid);
    let next;

    if (exists) {
      next = selectedAddons.filter((a) => a.factorid !== factor.factorid);
    } else {
      next = [
        ...selectedAddons,
        {
          factorid: factor.factorid,
          factorname: factor.factorname,
          value: factor.default_value || 0,
          value_type: factor.value_type,
          factor_type: factor.factor_type,
        },
      ];
    }

    dispatch(setSelectedAddons(next));

    const ids = [
      ...(selectedBedType?.factorid ? [selectedBedType.factorid] : []),
      ...next.map((a) => a.factorid),
    ];
    dispatch(setSelectedFactorIds(ids));
  };

  // ── 8. Date / guest handlers ────────────────────────────────────────
  const handleDateChange = (range) => {
    if (range.length === 2) {
      const [start, end] = range;
      const checkinDate = new Date(start);
      const checkoutDate = new Date(end);
      checkinDate.setHours(0, 0, 0, 0);
      checkoutDate.setHours(0, 0, 0, 0);
      if (checkinDate.getTime() === checkoutDate.getTime()) {
        checkoutDate.setDate(checkoutDate.getDate() + 1);
      }
      dispatch(setModifyDates([checkinDate, checkoutDate]));
    }
  };

  const handleCounterChange = (e, type, value) => {
    e.stopPropagation();
    if (value >= 0) {
      if (type === 'children') {
        dispatch({ type: 'SET_CHILDREN', payload: value });
        const newChildrenAges = [...childrenAges];
        if (value > childrenAges.length) {
          while (newChildrenAges.length < value) newChildrenAges.push(5);
        } else {
          newChildrenAges.length = value;
        }
        dispatch({ type: 'SET_CHILDREN_AGES', payload: newChildrenAges });
      } else {
        dispatch({ type: `SET_${type.toUpperCase()}`, payload: value });
      }
    }
  };

  const handleAgeChange = (index, value) => {
    const updatedAges = [...childrenAges];
    updatedAges[index] = parseInt(value);
    dispatch({ type: 'SET_CHILDREN_AGES', payload: updatedAges });
  };

  // ── 9. Special rate handlers ────────────────────────────────────────
  const validateContract = async (contractId) => {
    if (!contractId || contractId.trim() === '') {
      setContractValidation({
        isValid: false,
        message: 'Please enter a contract ID',
        data: null,
      });
      return false;
    }
    setIsValidatingContract(true);
    setContractValidation({
      isValid: null,
      message: 'Validating contract...',
      data: null,
    });
    try {
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/guest/contracts/${encodeURIComponent(contractId)}/validate`
      );
      const validationResult = await response.json();
      if (validationResult.status === 'valid') {
        setContractValidation({
          isValid: true,
          message: validationResult.message,
          data: validationResult,
        });
        return true;
      }
      setContractValidation({
        isValid: false,
        message: validationResult.message,
        data: validationResult,
      });
      return false;
    } catch (error) {
      console.error('Contract validation error:', error);
      setContractValidation({
        isValid: false,
        message: 'Failed to validate contract. Please try again.',
        data: null,
      });
      return false;
    } finally {
      setIsValidatingContract(false);
    }
  };

  const handleApplyCorporateCode = async () => {
    if (!localCorporateCode.trim()) {
      setContractValidation({
        isValid: false,
        message: 'Please enter a contract ID',
        data: null,
      });
      return;
    }
    if (localSeniorDiscount) {
      setLocalSeniorDiscount(false);
      dispatch({ type: 'SET_SENIOR_CITIZEN_DISCOUNT', payload: false });
    }
    const isValid = await validateContract(localCorporateCode.trim());
    if (isValid) {
      dispatch({ type: 'SET_CORPORATE_CODE', payload: localCorporateCode.trim() });
      dispatch({ type: 'SET_SPECIAL_RATE_APPLIED', payload: true });
      setShowSpecialRateDropdown(false);
      setShowDetails(true);
    }
  };

  const handleClearSpecialRates = () => {
    dispatch({ type: 'CLEAR_SPECIAL_RATES' });
    setLocalCorporateCode('');
    setLocalPromoCode('');
    setLocalSeniorDiscount(false);
    setShowDetails(false);
    setShowSpecialRateDropdown(false);
    setContractValidation({ isValid: null, message: '', data: null });
  };

  const getSpecialRateDisplayText = () => {
    if (isSpecialRateApplied) {
      const hasSenior = seniorCitizenDiscount;
      const hasCorporate = corporateCode && corporateCode.trim() !== '';
      if (hasSenior) return 'Senior Discount';
      if (hasCorporate) return `Corporate: ${corporateCode}`;
      if (promoCode && promoCode.trim() !== '') return `Promo: ${promoCode}`;
    }
    return 'Select Special Rate';
  };

  const isAnyRateApplied =
    isSpecialRateApplied && (seniorCitizenDiscount || corporateCode || promoCode);
  const isCorporateCodeApplied = corporateCode && corporateCode.trim() !== '';
  const isSeniorCitizenApplied = seniorCitizenDiscount;

  // ── 10. Click-outside handler ──────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCalendar && calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
      if (
        showTravellersDropdown &&
        travellersRef.current &&
        !travellersRef.current.contains(event.target)
      ) {
        setShowTravellersDropdown(false);
      }
      if (showRoomsDropdown && roomsRef.current && !roomsRef.current.contains(event.target)) {
        setShowRoomsDropdown(false);
      }
      if (
        showSpecialRateDropdown &&
        specialRateRef.current &&
        !specialRateRef.current.contains(event.target)
      ) {
        setShowSpecialRateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendar, showTravellersDropdown, showRoomsDropdown, showSpecialRateDropdown]);

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toDateString();
  };

  const today = new Date();
  const minDate = bookingData?.checkindate ? new Date(bookingData.checkindate) : today;
  const maxDate = new Date(minDate);
  maxDate.setFullYear(maxDate.getFullYear() + 1);

  // ── 11. Base + final price for current room ────────────────────────
  const baseRoomPrice = currentRoomPricing?.baseprice
    ? Number(currentRoomPricing.baseprice)
    : Number(bookingData?.baseprice || 0);

  const factorsFlatTotal = currentRoomPricing?.flat_total
    ? Number(currentRoomPricing.flat_total)
    : 0;

  const factorsPercentTotal = currentRoomPricing?.percentage_total
    ? Number(currentRoomPricing.percentage_total)
    : 0;

  const roomFinalPrice = currentRoomPricing?.final_price
    ? Number(currentRoomPricing.final_price)
    : baseRoomPrice + factorsFlatTotal + factorsPercentTotal;

  const roomNotApplicable = currentRoomPricing?.status === 'NOT_APPLICABLE';

  return (
    <div className="mt-6">
      <div className="px-5">
        <h1 className="text-2xl text-black font-bold mb-1">
          {bookingData ? 'Modify Your Reservation' : 'Select Your Room'}
        </h1>
        <p>
          {bookingData
            ? 'Modify your stay details and change your room preferences'
            : 'Enter your stay details and reserve your preferred room'}
        </p>
      </div>

      {contractValidation.isValid === false && (
        <div className="px-5 mt-2">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r">
            <div className="flex">
              <div className="flex-shrink-0">
                <FaExclamationTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-semibold text-red-700">{contractValidation.message}</p>
                {contractValidation.data?.action && (
                  <p className="text-xs text-red-600 mt-1">{contractValidation.data.action}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {contractValidation.isValid === true && (
        <div className="px-5 mt-2">
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r">
            <div className="flex">
              <div className="flex-shrink-0">
                <FaCheck className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-semibold text-green-700">{contractValidation.message}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Row 1 */}
      <div className="px-5 rounded-lg mt-4 relative flex gap-2">
        {/* Date */}
        <div
          className="flex flex-col border border-gray-700 p-3 rounded-md relative cursor-pointer w-1/3"
          onClick={() => {
            setShowCalendar(true);
            setShowTravellersDropdown(false);
            setShowRoomsDropdown(false);
            setShowSpecialRateDropdown(false);
          }}
        >
          <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
            Check-in & Check-Out Dates
          </label>
          <div className="flex items-center space-x-3">
            <FaCalendarAlt size={20} className="text-gray-800 md:w-4 sm:w-3" />
            <span className="text-gray-800 font-poppins text-base md:text-sm sm:text-xs font-semibold">
              {checkInDate && checkOutDate
                ? `${formatDisplayDate(checkInDate)} - ${formatDisplayDate(checkOutDate)}`
                : 'Select Dates'}
            </span>
            <div className="absolute top-2 right-2 p-2 rounded-md text-xs text-white font-semibold bg-black">
              {checkInDate && checkOutDate
                ? `(${daysCount} ${daysCount === 1 ? 'Night' : 'Nights'})`
                : ''}
            </div>
          </div>
          {showCalendar && (
            <div
              ref={calendarRef}
              className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10"
            >
              <Calendar
                onChange={handleDateChange}
                value={
                  checkInDate && checkOutDate ? [new Date(checkInDate), new Date(checkOutDate)] : []
                }
                selectRange={true}
                className="react-calendar p-2 mt-4 bg-white w-full"
                minDate={minDate}
                maxDate={maxDate}
                prev2Label={null}
                next2Label={null}
                minDetail="year"
              />
            </div>
          )}
        </div>

        {/* Guests */}
        <div
          ref={travellersRef}
          className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/3"
          onClick={() => {
            setShowTravellersDropdown(!showTravellersDropdown);
            setShowCalendar(false);
            setShowRoomsDropdown(false);
            setShowSpecialRateDropdown(false);
          }}
        >
          <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
            Guests
          </label>
          <div className="flex items-center space-x-2">
            <FaUser className="text-gray-800" />
            <span className="text-gray-800 text-base md:text-sm sm:text-xs font-poppins font-semibold">
              {adults} Adults{children > 0 ? `, ${children} Children` : ''}
            </span>
          </div>
          {showTravellersDropdown && (
            <div
              className="absolute top-20 left-0 bg-white shadow-lg font-poppins border rounded-md p-4 z-10 w-80"
              onClick={(e) => e.stopPropagation()}
            >
              {['adults', 'children'].map((type) => {
                const value = { adults, children }[type];
                const minValue = type === 'adults' ? 1 : 0;
                return (
                  <div key={type} className="flex justify-between items-center py-2">
                    <span className="text-black capitalize font-semibold">{type}</span>
                    <div className="flex items-center space-x-4">
                      <button
                        className={`px-4 py-2 rounded-full transition-colors ${
                          value <= minValue
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                        onClick={(e) => value > minValue && handleCounterChange(e, type, value - 1)}
                        disabled={value <= minValue}
                      >
                        -
                      </button>
                      <span className="text-lg font-semibold">{value}</span>
                      <button
                        className="px-3.5 py-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                        onClick={(e) => handleCounterChange(e, type, value + 1)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}

              {children > 0 && (
                <div className="mt-1">
                  <p className="text-gray-500 text-sm mb-3">
                    You can add children below 5 years old
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {childrenAges.map((age, index) => (
                      <div key={index} className="flex flex-col">
                        <label className="text-sm text-gray-700 mb-1">Child {index + 1} Age</label>
                        <select
                          value={age}
                          onChange={(e) => handleAgeChange(index, e.target.value)}
                          className="border border-gray-300 rounded-md p-2"
                        >
                          {Array.from({ length: 5 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                              {i + 1} {i === 0 ? 'year' : 'years'} old
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                className="w-full bg-black text-white px-4 py-2 mt-3 rounded-md font-poppins"
                onClick={() => setShowTravellersDropdown(false)}
              >
                Done
              </button>
            </div>
          )}
        </div>

        {/* Special Rate - COMMENTED OUT */}
        {/*
        <div
          ref={specialRateRef}
          className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/5"
          onClick={() => {
            setShowSpecialRateDropdown(!showSpecialRateDropdown);
            setShowCalendar(false);
            setShowTravellersDropdown(false);
            setShowRoomsDropdown(false);
          }}
        >
          ...
        </div>
        */}

        {/* Rooms */}
        <div
          ref={roomsRef}
          className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/3"
          onClick={() => {
            setShowRoomsDropdown(!showRoomsDropdown);
            setShowCalendar(false);
            setShowTravellersDropdown(false);
            setShowSpecialRateDropdown(false);
          }}
        >
          <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
            Rooms
          </label>
          <div className="flex items-center space-x-2">
            <MdBedroomParent size={24} className="text-gray-800" />
            <span className="text-gray-800 text-base md:text-sm sm:text-xs font-poppins font-semibold">
              {rooms} Room(s)
            </span>
          </div>
          {showRoomsDropdown && (
            <div className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10 w-76">
              <div className="flex justify-between items-center py-2">
                <span className="text-black font-poppins capitalize font-semibold">Rooms</span>
                <div className="flex items-center space-x-4">
                  <button
                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
                    onClick={(e) => handleCounterChange(e, 'rooms', Math.max(1, rooms - 1))}
                  >
                    -
                  </button>
                  <span className="text-lg font-semibold">{rooms}</span>
                  <button
                    className={`px-3.5 py-2 rounded-full ${
                      rooms >= 1
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                    onClick={(e) => rooms < 1 && handleCounterChange(e, 'rooms', rooms + 1)}
                    disabled={rooms >= 1}
                  >
                    +
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-900 mt-2 bg-yellow-200 px-2 py-1 rounded-sm">
                Reservations are limited to one room each.
              </p>
              <button
                className="w-full bg-black text-white px-4 py-2 mt-3 font-poppins rounded-md"
                onClick={() => setShowRoomsDropdown(false)}
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bed Type row */}
      {bedTypeFactors.length > 0 && (
        <div className="px-5 mt-4">
          <div className="flex items-center gap-3 border border-gray-300 rounded-md px-4 py-3 bg-white flex-wrap">
            <span className="font-semibold text-gray-800 text-sm">Bed Type:</span>
            <div className="flex flex-wrap gap-2">
              {bedTypeFactors.map((f) => {
                const isActive = selectedBedType?.factorid === f.factorid;
                const contribution = getFactorContributionForCurrentRoom(f.factorid);
                return (
                  <button
                    key={f.factorid}
                    onClick={() => handleBedTypeSelect(f)}
                    className={`px-4 py-2 rounded-md text-sm font-medium border transition ${
                      isActive
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
                    }`}
                  >
                    {f.factorname}
                    {contribution > 0 && (
                      <span
                        className={`ml-2 text-xs ${isActive ? 'text-gray-200' : 'text-gray-500'}`}
                      >
                        +{formatCurrency(contribution)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Add-ons row — discount factors hidden in UI only */}
      {addonFactors.length > 0 && (
        <div className="px-5 mt-3">
          <div className="flex items-center gap-3 border border-gray-300 rounded-md px-4 py-3 bg-white flex-wrap">
            <span className="font-semibold text-gray-800 text-sm">Add-ons & Features:</span>
            <div className="flex flex-wrap gap-3">
              {addonFactors.map((f) => {
                const checked = selectedAddons.some((a) => a.factorid === f.factorid);
                const contribution = getFactorContributionForCurrentRoom(f.factorid);
                const displayPrice = contribution || f.default_value || 0;

                return (
                  <label
                    key={f.factorid}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm cursor-pointer transition ${
                      checked ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleAddonToggle(f)}
                      className="accent-black"
                    />
                    <span className="text-gray-800">
                      {f.factorname}
                      {displayPrice > 0 && (
                        <span className={`ml-1 ${checked ? 'text-gray-900' : 'text-gray-500'}`}>
                          +{formatCurrency(displayPrice)}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomDates;

// import React, { useState, useEffect, useRef } from 'react';
// import Calendar from 'react-calendar';
// import 'react-calendar/dist/Calendar.css';
// import {
//   FaCalendarAlt,
//   FaUser,
//   FaBuilding,
//   FaPercent,
//   FaAngleDown,
//   FaAngleUp,
//   FaCheck,
//   FaTimes,
//   FaExclamationTriangle,
// } from 'react-icons/fa';
// import { useSelector, useDispatch } from 'react-redux';
// import { MdBedroomParent } from 'react-icons/md';
// import {
//   setModifyDates,
//   setAvailableFactors,
//   setSelectedBedType,
//   setSelectedAddons,
//   setSelectedFactorIds,
// } from '../Walk-In/redux/action';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL || 'http://localhost:9000';
// const BQ_PRICING_URL = process.env.REACT_APP_BQ_PRICING_URL || 'http://127.0.0.1:8001';

// const HIDDEN_ADDON_FACTOR_NAMES = ['number of guest', 'no of people', 'number of people'];

// const isHiddenAddon = (factor) => {
//   const name = (factor?.factorname || '').toLowerCase().trim();
//   return HIDDEN_ADDON_FACTOR_NAMES.some((hidden) => name.includes(hidden));
// };

// const formatDateParam = (d) => {
//   const dt = new Date(d);
//   const yyyy = dt.getFullYear();
//   const mm = String(dt.getMonth() + 1).padStart(2, '0');
//   const dd = String(dt.getDate()).padStart(2, '0');
//   return `${yyyy}-${mm}-${dd}`;
// };

// const formatCurrency = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// const RoomDates = () => {
//   const dispatch = useDispatch();

//   const { checkInDate, checkOutDate, daysCount } = useSelector(
//     (state) => state.selectedModifyDates
//   );

//   const { adults, children, rooms, childrenAges } = useSelector((state) => state.formDetails);

//   const bookingData = useSelector((state) => state.booking.selectedBooking);

//   const bookingFactors = useSelector((state) => state.factors?.bookingFactors || []);
//   const availableFactors = useSelector((state) => state.factors?.availableFactors || []);
//   const selectedBedType = useSelector((state) => state.factors?.selectedBedType || null);
//   const selectedAddons = useSelector((state) => state.factors?.selectedAddons || []);

//   const { corporateCode, seniorCitizenDiscount, promoCode, isSpecialRateApplied } = useSelector(
//     (state) => state.specialRates || {}
//   );

//   const [showCalendar, setShowCalendar] = useState(false);
//   const [showTravellersDropdown, setShowTravellersDropdown] = useState(false);
//   const [showRoomsDropdown, setShowRoomsDropdown] = useState(false);
//   const [showSpecialRateDropdown, setShowSpecialRateDropdown] = useState(false);

//   const [localCorporateCode, setLocalCorporateCode] = useState(corporateCode || '');
//   const [localPromoCode, setLocalPromoCode] = useState(promoCode || '');
//   const [localSeniorDiscount, setLocalSeniorDiscount] = useState(seniorCitizenDiscount || false);
//   const [showDetails, setShowDetails] = useState(false);

//   const [isValidatingContract, setIsValidatingContract] = useState(false);
//   const [contractValidation, setContractValidation] = useState({
//     isValid: null,
//     message: '',
//     data: null,
//   });

//   const [availabilityPricing, setAvailabilityPricing] = useState(null);
//   const [isLoadingPricing, setIsLoadingPricing] = useState(false);

//   const calendarRef = useRef(null);
//   const travellersRef = useRef(null);
//   const roomsRef = useRef(null);
//   const specialRateRef = useRef(null);

//   // ── 1. Initialise dates from booking ────────────────────────────────
//   useEffect(() => {
//     if (bookingData?.checkindate && bookingData?.checkoutdate) {
//       const checkinDate = new Date(bookingData.checkindate);
//       const checkoutDate = new Date(bookingData.checkoutdate);

//       dispatch(setModifyDates([checkinDate, checkoutDate]));

//       dispatch({
//         type: 'SET_ADULTS',
//         payload: bookingData.number_of_guests || 1,
//       });
//       dispatch({ type: 'SET_CHILDREN', payload: 0 });
//       dispatch({ type: 'SET_CHILDREN_AGES', payload: [] });
//       dispatch({ type: 'SET_ROOMS', payload: bookingData.rooms || 1 });
//     } else {
//       const today = new Date();
//       const tomorrow = new Date(today);
//       tomorrow.setDate(today.getDate() + 1);
//       dispatch(setModifyDates([today, tomorrow]));
//     }
//   }, [bookingData, dispatch]);

//   // ── 2. Fetch available factors ──────────────────────────────────────
//   useEffect(() => {
//     const fetchFactors = async () => {
//       try {
//         const res = await fetch(`${BQ_PRICING_URL}/pricing/api/get-factors`, {
//           headers: { Accept: 'application/json' },
//         });
//         if (!res.ok) return;
//         const data = await res.json();
//         dispatch(setAvailableFactors(Array.isArray(data) ? data : []));
//       } catch (err) {
//         console.error('Error fetching factors:', err);
//       }
//     };
//     if (availableFactors.length === 0) fetchFactors();
//   }, [dispatch, availableFactors.length]);

//   // ── 3. Pre-select factors from booking ──────────────────────────────
//   useEffect(() => {
//     if (!bookingFactors || bookingFactors.length === 0) return;

//     const bedTypeFromBooking = bookingFactors.find((f) => f.factor_type === 'bedtype');
//     const addonsFromBooking = bookingFactors.filter(
//       (f) => f.factor_type !== 'bedtype' && !isHiddenAddon({ factorname: f.factor_name })
//     );

//     if (bedTypeFromBooking) {
//       dispatch(
//         setSelectedBedType({
//           factorid: bedTypeFromBooking.factor_id,
//           factorname: bedTypeFromBooking.factor_name,
//           value: bedTypeFromBooking.value_applied,
//           value_type: bedTypeFromBooking.value_type,
//           factor_type: bedTypeFromBooking.factor_type,
//         })
//       );
//     }

//     if (addonsFromBooking.length > 0) {
//       dispatch(
//         setSelectedAddons(
//           addonsFromBooking.map((f) => ({
//             factorid: f.factor_id,
//             factorname: f.factor_name,
//             value: f.value_applied,
//             value_type: f.value_type,
//             factor_type: f.factor_type,
//           }))
//         )
//       );
//     }
//   }, [bookingFactors, dispatch]);

//   // ── 4. Sync special-rate local state ────────────────────────────────
//   useEffect(() => {
//     setLocalCorporateCode(corporateCode || '');
//     setLocalPromoCode(promoCode || '');
//     setLocalSeniorDiscount(seniorCitizenDiscount || false);
//   }, [corporateCode, promoCode, seniorCitizenDiscount]);

//   useEffect(() => {
//     if (contractValidation.isValid === true && !corporateCode) {
//       setContractValidation({ isValid: null, message: '', data: null });
//     }
//   }, [corporateCode]);

//   // ── 5. Derived factor lists ─────────────────────────────────────────
//   const bedTypeFactors = availableFactors.filter(
//     (f) => f.factor_type === 'bedtype' && f.isactive !== false
//   );

//   const addonFactors = availableFactors.filter(
//     (f) =>
//       f.show_in_bq === true &&
//       f.factor_type !== 'bedtype' &&
//       f.isactive !== false &&
//       !isHiddenAddon(f)
//   );

//   // Set of all bed-type factor ids (for de-duping)
//   const bedTypeFactorIdSet = new Set(bedTypeFactors.map((f) => Number(f.factorid)));

//   // Effective factor ids sent to availability API
//   const effectiveFactorIds = (() => {
//     const ids = new Set();

//     const currentBedTypeId = selectedBedType?.factorid ? Number(selectedBedType.factorid) : null;
//     if (currentBedTypeId) ids.add(currentBedTypeId);

//     for (const a of selectedAddons) {
//       const aid = Number(a.factorid);
//       if (bedTypeFactorIdSet.has(aid) && aid !== currentBedTypeId) continue;
//       ids.add(aid);
//     }

//     if (ids.size === 0) {
//       for (const f of bookingFactors) {
//         const fid = Number(f.factor_id);
//         if (Number.isFinite(fid)) ids.add(fid);
//       }
//     }

//     return Array.from(ids).filter((n) => Number.isFinite(n));
//   })();

//   // ── 6. Fetch availability pricing on date/factor change ────────────
//   useEffect(() => {
//     let isMounted = true;

//     const fetchAvailability = async () => {
//       try {
//         if (!checkInDate || !checkOutDate) return;

//         setIsLoadingPricing(true);
//         const params = new URLSearchParams();
//         params.set('checkin', formatDateParam(checkInDate));
//         params.set('checkout', formatDateParam(checkOutDate));
//         if (effectiveFactorIds.length > 0) {
//           params.set('factor_ids', effectiveFactorIds.join(','));
//         }

//         const res = await fetch(
//           `${BQ_BASE_URL}/bq/api/availability/check-dates/all?${params.toString()}`,
//           { headers: { Accept: 'application/json' } }
//         );
//         if (!res.ok) throw new Error('Availability request failed');

//         const data = await res.json();
//         if (!isMounted) return;
//         setAvailabilityPricing(data);
//         dispatch({ type: 'SET_AVAILABILITY_PRICING', payload: data });
//       } catch (err) {
//         console.error('Error fetching availability pricing:', err);
//         if (isMounted) setAvailabilityPricing(null);
//       } finally {
//         if (isMounted) setIsLoadingPricing(false);
//       }
//     };

//     fetchAvailability();
//     return () => {
//       isMounted = false;
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [checkInDate, checkOutDate, effectiveFactorIds.join(',')]);

//   // Current-room availability + pricing
//   const currentRoomTypeName = bookingData?.room_type || bookingData?.roomtypename || 'Comfort';

//   const currentRoomPricing = (() => {
//     if (!availabilityPricing?.room_types) return null;
//     return (
//       availabilityPricing.room_types.find(
//         (rt) => rt.room_type === currentRoomTypeName || rt.roomtypeid === bookingData?.roomtypeid
//       ) || null
//     );
//   })();

//   const getFactorContributionForCurrentRoom = (factorId) => {
//     if (!currentRoomPricing?.factor_breakdown) return 0;
//     const hit = currentRoomPricing.factor_breakdown.find(
//       (fb) => Number(fb.factorid) === Number(factorId)
//     );
//     return hit ? Number(hit.contribution || 0) : 0;
//   };

//   const currentRoomAvailableCount = currentRoomPricing?.available_rooms ?? null;
//   const currentRoomTotalCount = currentRoomPricing?.total_rooms ?? null;

//   // ── 7. Factor handlers ──────────────────────────────────────────────
//   const handleBedTypeSelect = (factor) => {
//     const newBedTypeId = Number(factor.factorid);

//     // Strip any other bed-type factor that may be in addons
//     const cleanedAddons = selectedAddons.filter((a) => !bedTypeFactorIdSet.has(Number(a.factorid)));

//     const payload = {
//       factorid: factor.factorid,
//       factorname: factor.factorname,
//       value: factor.default_value || 0,
//       value_type: factor.value_type,
//       factor_type: factor.factor_type,
//     };
//     dispatch(setSelectedBedType(payload));
//     dispatch(setSelectedAddons(cleanedAddons));

//     const ids = [newBedTypeId, ...cleanedAddons.map((a) => Number(a.factorid))];
//     dispatch(setSelectedFactorIds(ids));
//   };

//   const handleAddonToggle = (factor) => {
//     const exists = selectedAddons.some((a) => a.factorid === factor.factorid);
//     let next;

//     if (exists) {
//       next = selectedAddons.filter((a) => a.factorid !== factor.factorid);
//     } else {
//       next = [
//         ...selectedAddons,
//         {
//           factorid: factor.factorid,
//           factorname: factor.factorname,
//           value: factor.default_value || 0,
//           value_type: factor.value_type,
//           factor_type: factor.factor_type,
//         },
//       ];
//     }

//     dispatch(setSelectedAddons(next));

//     const ids = [
//       ...(selectedBedType?.factorid ? [selectedBedType.factorid] : []),
//       ...next.map((a) => a.factorid),
//     ];
//     dispatch(setSelectedFactorIds(ids));
//   };

//   // ── 8. Date / guest handlers ────────────────────────────────────────
//   const handleDateChange = (range) => {
//     if (range.length === 2) {
//       const [start, end] = range;
//       const checkinDate = new Date(start);
//       const checkoutDate = new Date(end);
//       checkinDate.setHours(0, 0, 0, 0);
//       checkoutDate.setHours(0, 0, 0, 0);
//       if (checkinDate.getTime() === checkoutDate.getTime()) {
//         checkoutDate.setDate(checkoutDate.getDate() + 1);
//       }
//       dispatch(setModifyDates([checkinDate, checkoutDate]));
//     }
//   };

//   const handleCounterChange = (e, type, value) => {
//     e.stopPropagation();
//     if (value >= 0) {
//       if (type === 'children') {
//         dispatch({ type: 'SET_CHILDREN', payload: value });
//         const newChildrenAges = [...childrenAges];
//         if (value > childrenAges.length) {
//           while (newChildrenAges.length < value) newChildrenAges.push(5);
//         } else {
//           newChildrenAges.length = value;
//         }
//         dispatch({ type: 'SET_CHILDREN_AGES', payload: newChildrenAges });
//       } else {
//         dispatch({ type: `SET_${type.toUpperCase()}`, payload: value });
//       }
//     }
//   };

//   const handleAgeChange = (index, value) => {
//     const updatedAges = [...childrenAges];
//     updatedAges[index] = parseInt(value);
//     dispatch({ type: 'SET_CHILDREN_AGES', payload: updatedAges });
//   };

//   // ── 9. Special rate handlers ────────────────────────────────────────
//   const validateContract = async (contractId) => {
//     if (!contractId || contractId.trim() === '') {
//       setContractValidation({
//         isValid: false,
//         message: 'Please enter a contract ID',
//         data: null,
//       });
//       return false;
//     }
//     setIsValidatingContract(true);
//     setContractValidation({
//       isValid: null,
//       message: 'Validating contract...',
//       data: null,
//     });
//     try {
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/guest/contracts/${encodeURIComponent(contractId)}/validate`
//       );
//       const validationResult = await response.json();
//       if (validationResult.status === 'valid') {
//         setContractValidation({
//           isValid: true,
//           message: validationResult.message,
//           data: validationResult,
//         });
//         return true;
//       }
//       setContractValidation({
//         isValid: false,
//         message: validationResult.message,
//         data: validationResult,
//       });
//       return false;
//     } catch (error) {
//       console.error('Contract validation error:', error);
//       setContractValidation({
//         isValid: false,
//         message: 'Failed to validate contract. Please try again.',
//         data: null,
//       });
//       return false;
//     } finally {
//       setIsValidatingContract(false);
//     }
//   };

//   const handleApplyCorporateCode = async () => {
//     if (!localCorporateCode.trim()) {
//       setContractValidation({
//         isValid: false,
//         message: 'Please enter a contract ID',
//         data: null,
//       });
//       return;
//     }
//     if (localSeniorDiscount) {
//       setLocalSeniorDiscount(false);
//       dispatch({ type: 'SET_SENIOR_CITIZEN_DISCOUNT', payload: false });
//     }
//     const isValid = await validateContract(localCorporateCode.trim());
//     if (isValid) {
//       dispatch({ type: 'SET_CORPORATE_CODE', payload: localCorporateCode.trim() });
//       dispatch({ type: 'SET_SPECIAL_RATE_APPLIED', payload: true });
//       setShowSpecialRateDropdown(false);
//       setShowDetails(true);
//     }
//   };

//   const handleClearSpecialRates = () => {
//     dispatch({ type: 'CLEAR_SPECIAL_RATES' });
//     setLocalCorporateCode('');
//     setLocalPromoCode('');
//     setLocalSeniorDiscount(false);
//     setShowDetails(false);
//     setShowSpecialRateDropdown(false);
//     setContractValidation({ isValid: null, message: '', data: null });
//   };

//   const getSpecialRateDisplayText = () => {
//     if (isSpecialRateApplied) {
//       const hasSenior = seniorCitizenDiscount;
//       const hasCorporate = corporateCode && corporateCode.trim() !== '';
//       if (hasSenior) return 'Senior Discount';
//       if (hasCorporate) return `Corporate: ${corporateCode}`;
//       if (promoCode && promoCode.trim() !== '') return `Promo: ${promoCode}`;
//     }
//     return 'Select Special Rate';
//   };

//   const isAnyRateApplied =
//     isSpecialRateApplied && (seniorCitizenDiscount || corporateCode || promoCode);
//   const isCorporateCodeApplied = corporateCode && corporateCode.trim() !== '';
//   const isSeniorCitizenApplied = seniorCitizenDiscount;

//   // ── 10. Click-outside handler ──────────────────────────────────────
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (showCalendar && calendarRef.current && !calendarRef.current.contains(event.target)) {
//         setShowCalendar(false);
//       }
//       if (
//         showTravellersDropdown &&
//         travellersRef.current &&
//         !travellersRef.current.contains(event.target)
//       ) {
//         setShowTravellersDropdown(false);
//       }
//       if (showRoomsDropdown && roomsRef.current && !roomsRef.current.contains(event.target)) {
//         setShowRoomsDropdown(false);
//       }
//       if (
//         showSpecialRateDropdown &&
//         specialRateRef.current &&
//         !specialRateRef.current.contains(event.target)
//       ) {
//         setShowSpecialRateDropdown(false);
//       }
//     };
//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, [showCalendar, showTravellersDropdown, showRoomsDropdown, showSpecialRateDropdown]);

//   const formatDisplayDate = (dateString) => {
//     if (!dateString) return '';
//     return new Date(dateString).toDateString();
//   };

//   const today = new Date();
//   const minDate = bookingData?.checkindate ? new Date(bookingData.checkindate) : today;
//   const maxDate = new Date(minDate);
//   maxDate.setFullYear(maxDate.getFullYear() + 1);

//   // ── 11. Base + final price for current room ────────────────────────
//   const baseRoomPrice = currentRoomPricing?.baseprice
//     ? Number(currentRoomPricing.baseprice)
//     : Number(bookingData?.baseprice || 0);

//   const factorsFlatTotal = currentRoomPricing?.flat_total
//     ? Number(currentRoomPricing.flat_total)
//     : 0;

//   const factorsPercentTotal = currentRoomPricing?.percentage_total
//     ? Number(currentRoomPricing.percentage_total)
//     : 0;

//   const roomFinalPrice = currentRoomPricing?.final_price
//     ? Number(currentRoomPricing.final_price)
//     : baseRoomPrice + factorsFlatTotal + factorsPercentTotal;

//   const roomNotApplicable = currentRoomPricing?.status === 'NOT_APPLICABLE';

//   return (
//     <div className="mt-6">
//       <div className="px-5">
//         <h1 className="text-2xl text-black font-bold mb-1">
//           {bookingData ? 'Modify Your Reservation' : 'Select Your Room'}
//         </h1>
//         <p>
//           {bookingData
//             ? 'Modify your stay details and change your room preferences'
//             : 'Enter your stay details and reserve your preferred room'}
//         </p>
//       </div>

//       {contractValidation.isValid === false && (
//         <div className="px-5 mt-2">
//           <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r">
//             <div className="flex">
//               <div className="flex-shrink-0">
//                 <FaExclamationTriangle className="h-5 w-5 text-red-400" />
//               </div>
//               <div className="ml-3">
//                 <p className="text-sm font-semibold text-red-700">{contractValidation.message}</p>
//                 {contractValidation.data?.action && (
//                   <p className="text-xs text-red-600 mt-1">{contractValidation.data.action}</p>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {contractValidation.isValid === true && (
//         <div className="px-5 mt-2">
//           <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r">
//             <div className="flex">
//               <div className="flex-shrink-0">
//                 <FaCheck className="h-5 w-5 text-green-400" />
//               </div>
//               <div className="ml-3">
//                 <p className="text-sm font-semibold text-green-700">{contractValidation.message}</p>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Row 1 */}
//       <div className="px-5 rounded-lg mt-4 relative flex gap-2">
//         {/* Date */}
//         <div
//           className="flex flex-col border border-gray-700 p-3 rounded-md relative cursor-pointer w-1/3"
//           onClick={() => {
//             setShowCalendar(true);
//             setShowTravellersDropdown(false);
//             setShowRoomsDropdown(false);
//             setShowSpecialRateDropdown(false);
//           }}
//         >
//           <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
//             Check-in & Check-Out Dates
//           </label>
//           <div className="flex items-center space-x-3">
//             <FaCalendarAlt size={20} className="text-gray-800 md:w-4 sm:w-3" />
//             <span className="text-gray-800 font-poppins text-base md:text-sm sm:text-xs font-semibold">
//               {checkInDate && checkOutDate
//                 ? `${formatDisplayDate(checkInDate)} - ${formatDisplayDate(checkOutDate)}`
//                 : 'Select Dates'}
//             </span>
//             <div className="absolute top-2 right-2 p-2 rounded-md text-xs text-white font-semibold bg-black">
//               {checkInDate && checkOutDate
//                 ? `(${daysCount} ${daysCount === 1 ? 'Night' : 'Nights'})`
//                 : ''}
//             </div>
//           </div>
//           {showCalendar && (
//             <div
//               ref={calendarRef}
//               className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10"
//             >
//               <Calendar
//                 onChange={handleDateChange}
//                 value={
//                   checkInDate && checkOutDate ? [new Date(checkInDate), new Date(checkOutDate)] : []
//                 }
//                 selectRange={true}
//                 className="react-calendar p-2 mt-4 bg-white w-full"
//                 minDate={minDate}
//                 maxDate={maxDate}
//                 prev2Label={null}
//                 next2Label={null}
//                 minDetail="year"
//               />
//             </div>
//           )}
//         </div>

//         {/* Guests */}
//         <div
//           ref={travellersRef}
//           className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/3"
//           onClick={() => {
//             setShowTravellersDropdown(!showTravellersDropdown);
//             setShowCalendar(false);
//             setShowRoomsDropdown(false);
//             setShowSpecialRateDropdown(false);
//           }}
//         >
//           <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
//             Guests
//           </label>
//           <div className="flex items-center space-x-2">
//             <FaUser className="text-gray-800" />
//             <span className="text-gray-800 text-base md:text-sm sm:text-xs font-poppins font-semibold">
//               {adults} Adults{children > 0 ? `, ${children} Children` : ''}
//             </span>
//           </div>
//           {showTravellersDropdown && (
//             <div
//               className="absolute top-20 left-0 bg-white shadow-lg font-poppins border rounded-md p-4 z-10 w-80"
//               onClick={(e) => e.stopPropagation()}
//             >
//               {['adults', 'children'].map((type) => {
//                 const value = { adults, children }[type];
//                 const minValue = type === 'adults' ? 1 : 0;
//                 return (
//                   <div key={type} className="flex justify-between items-center py-2">
//                     <span className="text-black capitalize font-semibold">{type}</span>
//                     <div className="flex items-center space-x-4">
//                       <button
//                         className={`px-4 py-2 rounded-full transition-colors ${
//                           value <= minValue
//                             ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
//                             : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
//                         }`}
//                         onClick={(e) => value > minValue && handleCounterChange(e, type, value - 1)}
//                         disabled={value <= minValue}
//                       >
//                         -
//                       </button>
//                       <span className="text-lg font-semibold">{value}</span>
//                       <button
//                         className="px-3.5 py-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
//                         onClick={(e) => handleCounterChange(e, type, value + 1)}
//                       >
//                         +
//                       </button>
//                     </div>
//                   </div>
//                 );
//               })}

//               {children > 0 && (
//                 <div className="mt-1">
//                   <p className="text-gray-500 text-sm mb-3">
//                     You can add children below 5 years old
//                   </p>
//                   <div className="grid grid-cols-2 gap-3">
//                     {childrenAges.map((age, index) => (
//                       <div key={index} className="flex flex-col">
//                         <label className="text-sm text-gray-700 mb-1">Child {index + 1} Age</label>
//                         <select
//                           value={age}
//                           onChange={(e) => handleAgeChange(index, e.target.value)}
//                           className="border border-gray-300 rounded-md p-2"
//                         >
//                           {Array.from({ length: 5 }, (_, i) => (
//                             <option key={i + 1} value={i + 1}>
//                               {i + 1} {i === 0 ? 'year' : 'years'} old
//                             </option>
//                           ))}
//                         </select>
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               )}

//               <button
//                 className="w-full bg-black text-white px-4 py-2 mt-3 rounded-md font-poppins"
//                 onClick={() => setShowTravellersDropdown(false)}
//               >
//                 Done
//               </button>
//             </div>
//           )}
//         </div>

//         {/* Special Rate - COMMENTED OUT */}
//         {/*
//         <div
//           ref={specialRateRef}
//           className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/5"
//           onClick={() => {
//             setShowSpecialRateDropdown(!showSpecialRateDropdown);
//             setShowCalendar(false);
//             setShowTravellersDropdown(false);
//             setShowRoomsDropdown(false);
//           }}
//         >
//           <div
//             className="flex justify-between items-center cursor-pointer"
//             onClick={() => setShowDetails(!showDetails)}
//           >
//             <div className="flex flex-col">
//               <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
//                 Special Rate
//               </label>
//               <div className="flex items-center space-x-1">
//                 <FaPercent className="text-gray-800" size={12} />
//                 <span className="text-gray-800 text-xs font-poppins font-semibold">
//                   {getSpecialRateDisplayText()}
//                 </span>
//               </div>
//             </div>
//             <div className="flex items-center">{showDetails ? <FaAngleUp /> : <FaAngleDown />}</div>
//           </div>

//           {showSpecialRateDropdown && (
//             <div
//               className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10 w-76"
//               onClick={(e) => e.stopPropagation()}
//             >
//               <div className="mb-4">
//                 <h3 className="text-sm font-bold text-gray-800 mb-3">Select Special Rate</h3>

//                 <div
//                   className={`mb-4 p-3 border border-gray-200 rounded-md ${
//                     isSeniorCitizenApplied ? 'bg-gray-50' : 'hover:bg-gray-50'
//                   }`}
//                 >
//                   <div className="flex items-start space-x-3">
//                     <FaBuilding
//                       className={`mt-1 text-xl ${
//                         isSeniorCitizenApplied ? 'text-gray-400' : 'text-green-600'
//                       }`}
//                     />
//                     <div className="flex-1">
//                       <label
//                         className={`font-semibold text-gray-700 mb-1 block text-sm ${
//                           isSeniorCitizenApplied ? 'text-gray-500' : 'text-gray-700'
//                         }`}
//                       >
//                         Special code
//                       </label>
//                       <div className="flex space-x-2">
//                         <input
//                           type="text"
//                           value={localCorporateCode}
//                           onChange={(e) => {
//                             setLocalCorporateCode(e.target.value);
//                             if (contractValidation.isValid !== null) {
//                               setContractValidation({
//                                 isValid: null,
//                                 message: '',
//                                 data: null,
//                               });
//                             }
//                           }}
//                           placeholder="Enter special code"
//                           className={`flex-1 border border-gray-300 rounded-md py-1 px-2 text-sm ${
//                             isSeniorCitizenApplied || isValidatingContract || isCorporateCodeApplied
//                               ? 'bg-gray-100 cursor-not-allowed'
//                               : ''
//                           }`}
//                           disabled={
//                             isSeniorCitizenApplied || isValidatingContract || isCorporateCodeApplied
//                           }
//                         />
//                         <button
//                           onClick={handleApplyCorporateCode}
//                           disabled={
//                             !localCorporateCode.trim() ||
//                             isValidatingContract ||
//                             isSeniorCitizenApplied ||
//                             isCorporateCodeApplied
//                           }
//                           className={`px-4 py-2 rounded-md text-sm transition ${
//                             localCorporateCode.trim() &&
//                             !isValidatingContract &&
//                             !isSeniorCitizenApplied &&
//                             !isCorporateCodeApplied
//                               ? 'bg-black text-white hover:bg-gray-900'
//                               : 'bg-gray-300 text-gray-500 cursor-not-allowed'
//                           }`}
//                         >
//                           {isValidatingContract ? 'Validating...' : 'Apply'}
//                         </button>
//                       </div>
//                       <p className="text-xs text-gray-500 mt-1">
//                         Enter your hotel code for corporate rates
//                         {isSeniorCitizenApplied && (
//                           <span className="text-red-500 ml-1">(Clear senior discount first)</span>
//                         )}
//                       </p>

//                       {contractValidation.isValid !== null && (
//                         <div className="mt-2 text-xs">
//                           {contractValidation.isValid === true ? (
//                             <span className="text-green-600 flex items-center">
//                               <FaCheck className="mr-1" /> Contract code is valid
//                             </span>
//                           ) : contractValidation.isValid === false ? (
//                             <span className="text-red-600 flex items-center">
//                               <FaTimes className="mr-1" /> Invalid contract code
//                             </span>
//                           ) : null}
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 </div>

//                 {isAnyRateApplied && (
//                   <div className="mt-4 pt-3 border-t border-gray-200">
//                     <button
//                       onClick={handleClearSpecialRates}
//                       className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm hover:bg-gray-300 transition"
//                     >
//                       Clear Special Rate
//                     </button>
//                   </div>
//                 )}

//                 <button
//                   className="w-full bg-black text-white px-4 py-2 mt-2 rounded-md text-sm font-poppins hover:bg-gray-800 transition"
//                   onClick={() => setShowSpecialRateDropdown(false)}
//                 >
//                   Done
//                 </button>
//               </div>
//             </div>
//           )}
//         </div>
//         */}

//         {/* Rooms */}
//         <div
//           ref={roomsRef}
//           className="flex flex-col border border-gray-800 p-3 rounded-md relative cursor-pointer w-1/3"
//           onClick={() => {
//             setShowRoomsDropdown(!showRoomsDropdown);
//             setShowCalendar(false);
//             setShowTravellersDropdown(false);
//             setShowSpecialRateDropdown(false);
//           }}
//         >
//           <label className="text-gray-800 text-lg md:text-base sm:text-sm font-poppins font-bold">
//             Rooms
//           </label>
//           <div className="flex items-center space-x-2">
//             <MdBedroomParent size={24} className="text-gray-800" />
//             <span className="text-gray-800 text-base md:text-sm sm:text-xs font-poppins font-semibold">
//               {rooms} Room(s)
//             </span>
//           </div>
//           {showRoomsDropdown && (
//             <div className="absolute top-20 left-0 bg-white shadow-lg border rounded-md p-4 z-10 w-76">
//               <div className="flex justify-between items-center py-2">
//                 <span className="text-black font-poppins capitalize font-semibold">Rooms</span>
//                 <div className="flex items-center space-x-4">
//                   <button
//                     className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
//                     onClick={(e) => handleCounterChange(e, 'rooms', Math.max(1, rooms - 1))}
//                   >
//                     -
//                   </button>
//                   <span className="text-lg font-semibold">{rooms}</span>
//                   <button
//                     className={`px-3.5 py-2 rounded-full ${
//                       rooms >= 1
//                         ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
//                         : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
//                     }`}
//                     onClick={(e) => rooms < 1 && handleCounterChange(e, 'rooms', rooms + 1)}
//                     disabled={rooms >= 1}
//                   >
//                     +
//                   </button>
//                 </div>
//               </div>
//               <p className="text-sm text-gray-900 mt-2 bg-yellow-200 px-2 py-1 rounded-sm">
//                 Reservations are limited to one room each.
//               </p>
//               <button
//                 className="w-full bg-black text-white px-4 py-2 mt-3 font-poppins rounded-md"
//                 onClick={() => setShowRoomsDropdown(false)}
//               >
//                 Done
//               </button>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Bed Type row */}
//       {bedTypeFactors.length > 0 && (
//         <div className="px-5 mt-4">
//           <div className="flex items-center gap-3 border border-gray-300 rounded-md px-4 py-3 bg-white flex-wrap">
//             <span className="font-semibold text-gray-800 text-sm">Bed Type:</span>
//             <div className="flex flex-wrap gap-2">
//               {bedTypeFactors.map((f) => {
//                 const isActive = selectedBedType?.factorid === f.factorid;
//                 const contribution = getFactorContributionForCurrentRoom(f.factorid);
//                 return (
//                   <button
//                     key={f.factorid}
//                     onClick={() => handleBedTypeSelect(f)}
//                     className={`px-4 py-2 rounded-md text-sm font-medium border transition ${
//                       isActive
//                         ? 'bg-black text-white border-black'
//                         : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'
//                     }`}
//                   >
//                     {f.factorname}
//                     {contribution > 0 && (
//                       <span
//                         className={`ml-2 text-xs ${isActive ? 'text-gray-200' : 'text-gray-500'}`}
//                       >
//                         +{formatCurrency(contribution)}
//                       </span>
//                     )}
//                   </button>
//                 );
//               })}
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Add-ons row */}
//       {addonFactors.length > 0 && (
//         <div className="px-5 mt-3">
//           <div className="flex items-center gap-3 border border-gray-300 rounded-md px-4 py-3 bg-white flex-wrap">
//             <span className="font-semibold text-gray-800 text-sm">Add-ons & Features:</span>
//             <div className="flex flex-wrap gap-3">
//               {addonFactors.map((f) => {
//                 const checked = selectedAddons.some((a) => a.factorid === f.factorid);
//                 const contribution = getFactorContributionForCurrentRoom(f.factorid);
//                 const displayPrice = contribution || f.default_value || 0;

//                 return (
//                   <label
//                     key={f.factorid}
//                     className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm cursor-pointer transition ${
//                       checked ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-gray-500'
//                     }`}
//                   >
//                     <input
//                       type="checkbox"
//                       checked={checked}
//                       onChange={() => handleAddonToggle(f)}
//                       className="accent-black"
//                     />
//                     <span className="text-gray-800">
//                       {f.factorname}
//                       {displayPrice > 0 && (
//                         <span className={`ml-1 ${checked ? 'text-gray-900' : 'text-gray-500'}`}>
//                           +{formatCurrency(displayPrice)}
//                         </span>
//                       )}
//                     </span>
//                   </label>
//                 );
//               })}
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default RoomDates;
